/**
 * Pure color math: WCAG contrast, OKLab/OKLCH conversion, Machado-2009
 * color-vision-deficiency simulation, the brand contrast guard (spec §5.4)
 * and the chart palette selection rule (spec §7.1, as amended).
 *
 * No dependencies. Every color in and out is a 6-digit `#RRGGBB` hex string.
 */
// ---------------------------------------------------------------------------
// sRGB <-> linear
// ---------------------------------------------------------------------------
function hexToSrgb(hex) {
    const h = hex.replace(/^#/, '');
    return [parseInt(h.slice(0, 2), 16) / 255, parseInt(h.slice(2, 4), 16) / 255, parseInt(h.slice(4, 6), 16) / 255];
}
function srgbToLinearChannel(c) {
    return c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
}
/** Clamps to [0, 1] before applying the inverse gamma, so out-of-gamut
 *  channels (from `fromOklch`) round-trip to a legal byte instead of NaN. */
function linearToSrgbChannel(c) {
    const clamped = Math.min(1, Math.max(0, c));
    return clamped <= 0.0031308 ? 12.92 * clamped : 1.055 * clamped ** (1 / 2.4) - 0.055;
}
function hexToLinear(hex) {
    const [r, g, b] = hexToSrgb(hex);
    return [srgbToLinearChannel(r), srgbToLinearChannel(g), srgbToLinearChannel(b)];
}
function relativeLuminance(hex) {
    const [r, g, b] = hexToLinear(hex);
    return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}
/** WCAG 2.x contrast ratio between two colors, always >= 1. */
export function contrastRatio(a, b) {
    const la = relativeLuminance(a);
    const lb = relativeLuminance(b);
    const hi = Math.max(la, lb);
    const lo = Math.min(la, lb);
    return (hi + 0.05) / (lo + 0.05);
}
// ---------------------------------------------------------------------------
// OKLab / OKLCH
// ---------------------------------------------------------------------------
function oklabFromLinear([r, g, b]) {
    const l = Math.cbrt(0.4122214708 * r + 0.5363325363 * g + 0.0514459929 * b);
    const m = Math.cbrt(0.2119034982 * r + 0.6806995451 * g + 0.1073969566 * b);
    const s = Math.cbrt(0.0883024619 * r + 0.2817188376 * g + 0.6299787005 * b);
    return [
        0.2104542553 * l + 0.793617785 * m - 0.0040720468 * s,
        1.9779984951 * l - 2.428592205 * m + 0.4505937099 * s,
        0.0259040371 * l + 0.7827717662 * m - 0.808675766 * s
    ];
}
export function toOklch(hex) {
    const [l, a, b] = oklabFromLinear(hexToLinear(hex));
    const c = Math.hypot(a, b);
    const h = ((Math.atan2(b, a) * 180) / Math.PI + 360) % 360;
    return { l, c, h };
}
/** Gamut-clamped: out-of-range channels are clamped to [0, 1], not mapped, so
 *  callers driving this near the gamut edge (as `deriveBrandColors` does)
 *  should expect the result to sit exactly on the clamp rather than beyond it. */
export function fromOklch(l, c, h) {
    const hr = (h * Math.PI) / 180;
    const a = c * Math.cos(hr);
    const b = c * Math.sin(hr);
    const l_ = l + 0.3963377774 * a + 0.2158037573 * b;
    const m_ = l - 0.1055613458 * a - 0.0638541728 * b;
    const s_ = l - 0.0894841775 * a - 1.291485548 * b;
    const [L, M, S] = [l_ ** 3, m_ ** 3, s_ ** 3];
    const lin = [
        4.0767416621 * L - 3.3077115913 * M + 0.2309699292 * S,
        -1.2684380046 * L + 2.6097574011 * M - 0.3413193965 * S,
        -0.0041960863 * L - 0.7034186147 * M + 1.707614701 * S
    ];
    const toByte = (x) => Math.round(linearToSrgbChannel(x) * 255);
    return `#${lin.map(toByte).map((v) => v.toString(16).padStart(2, '0').toUpperCase()).join('')}`;
}
// ---------------------------------------------------------------------------
// Color-vision-deficiency simulation
// ---------------------------------------------------------------------------
/** Machado, Oliveira & Fernandes (2009) severity-1.0 transforms, linear RGB. */
const MACHADO = {
    protan: [
        [0.152286, 1.052583, -0.204868],
        [0.114503, 0.786281, 0.099216],
        [-0.003882, -0.048116, 1.051998]
    ],
    deutan: [
        [0.367322, 0.860646, -0.227968],
        [0.280085, 0.672501, 0.047413],
        [-0.01182, 0.04294, 0.968881]
    ]
};
function simulate(hex, kind) {
    const [r, g, b] = hexToLinear(hex);
    const m = MACHADO[kind];
    const clamp = (x) => Math.min(1, Math.max(0, x));
    return [
        clamp(m[0][0] * r + m[0][1] * g + m[0][2] * b),
        clamp(m[1][0] * r + m[1][1] * g + m[1][2] * b),
        clamp(m[2][0] * r + m[2][1] * g + m[2][2] * b)
    ];
}
/** Euclidean OKLab distance ×100; with `cvd`, both colors are simulated first
 *  (Machado, Oliveira & Fernandes 2009, severity 1.0, linear RGB). */
export function deltaE(a, b, cvd) {
    const linA = cvd ? simulate(a, cvd) : hexToLinear(a);
    const linB = cvd ? simulate(b, cvd) : hexToLinear(b);
    const [l1, a1, b1] = oklabFromLinear(linA);
    const [l2, a2, b2] = oklabFromLinear(linB);
    return 100 * Math.hypot(l1 - l2, a1 - a2, b1 - b2);
}
// ---------------------------------------------------------------------------
// Brand contrast guard (spec §5.4)
// ---------------------------------------------------------------------------
/** Bisects L (holding C and h fixed) for the lightest color that still meets
 *  `minContrast` against white. L = 0 always meets any threshold used here
 *  (black-on-white contrast is 21), so the search always converges. */
function darkenUntil(hex, minContrast) {
    if (contrastRatio(hex, '#FFFFFF') >= minContrast)
        return hex;
    const { c, h, l } = toOklch(hex);
    let lo = 0;
    let hi = l;
    let darkest = fromOklch(lo, c, h);
    for (let i = 0; i < 30; i++) {
        const mid = (lo + hi) / 2;
        const candidate = fromOklch(mid, c, h);
        if (contrastRatio(candidate, '#FFFFFF') >= minContrast) {
            darkest = candidate;
            lo = mid;
        }
        else {
            hi = mid;
        }
    }
    return darkest;
}
/** Darkens in OKLCH (lower L, same hue) until each threshold is met on white.
 *  A color that already passes is returned unchanged. */
export function deriveBrandColors(accent) {
    return { mark: darkenUntil(accent, 3), text: darkenUntil(accent, 4.5) };
}
// ---------------------------------------------------------------------------
// Chart palette selection (spec §7.1, as amended)
// ---------------------------------------------------------------------------
export const SERIES_POOL = [
    '#8A919A', '#6D4C9F', '#B7791F', '#3F6FB0', '#B4532E', // preferred order
    '#2A78D6', '#EB6834', '#1BAF7A', '#EDA100', '#E87BA4', '#008300', '#4A3AA7', '#E34948', '#5B6470' // reserves
];
const NORMAL_FLOOR = 15;
const CVD_TARGET = 8;
/** The adjacent-pair gate: normal-vision separation and CVD separation both
 *  have to clear their floors, or the pair is not a legal neighbour. */
function passesAdjacent(a, b) {
    if (deltaE(a, b) < NORMAL_FLOOR)
        return false;
    return Math.min(deltaE(a, b, 'protan'), deltaE(a, b, 'deutan')) >= CVD_TARGET;
}
/** brand + first 5 pool colors (pool order, backtracking) such that no pick is
 *  within ΔE 15 of the brand and every neighbour pair — brand→slot 2 included —
 *  has normal ΔE ≥ 15 and min(protan, deutan) ΔE ≥ 8. */
export function chartPalette(brandMark) {
    const candidates = SERIES_POOL.filter((c) => deltaE(c, brandMark) >= NORMAL_FLOOR);
    let best = null;
    function walk(seq, last, used) {
        if (best)
            return;
        if (seq.length === 6) {
            best = seq;
            return;
        }
        for (const [i, candidate] of candidates.entries()) {
            if (used.has(i) || best)
                continue;
            if (!passesAdjacent(candidate, last))
                continue;
            used.add(i);
            walk([...seq, candidate], candidate, used);
            used.delete(i);
        }
    }
    walk([brandMark], brandMark, new Set());
    return best ?? [brandMark];
}
//# sourceMappingURL=color.js.map